<script lang="ts">
  import { onMount } from 'svelte';
  import type { DeviceProfileSummary } from '@ucc/shared';
  import {
    ApiError,
    deleteDeviceProfile,
    fetchDeviceProfile,
    fetchDeviceProfiles,
    replaceDeviceProfile,
    uploadDeviceProfile,
  } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  let profiles = $state<DeviceProfileSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let formError = $state<string | null>(null);
  let saving = $state(false);
  let detailId = $state<string | null>(null);
  let detailJson = $state<string>('');
  let detailLoading = $state(false);

  const role = $derived(session.user?.role);
  const isStaff = $derived(role === 'admin' || role === 'user');

  onMount(() => {
    void reload();
  });

  async function reload() {
    loading = true;
    error = null;
    try {
      const res = await fetchDeviceProfiles();
      profiles = res.profiles ?? [];
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load profiles';
      profiles = [];
    } finally {
      loading = false;
    }
  }

  async function onUpload(e: Event) {
    e.preventDefault();
    formError = null;
    const input = (e.target as HTMLFormElement).elements.namedItem('file') as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      formError = 'Choose a .json profile file.';
      return;
    }
    saving = true;
    try {
      const text = await file.text();
      let json: unknown;
      try {
        json = JSON.parse(text);
      } catch {
        formError = 'File is not valid JSON.';
        return;
      }
      await uploadDeviceProfile({ json });
      if (input) input.value = '';
      await reload();
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Upload failed';
    } finally {
      saving = false;
    }
  }

  async function openDetail(id: string) {
    detailId = id;
    detailJson = '';
    detailLoading = true;
    formError = null;
    try {
      const res = await fetchDeviceProfile(id);
      detailJson = JSON.stringify(res.profile.json, null, 2);
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Failed to load profile';
      detailId = null;
    } finally {
      detailLoading = false;
    }
  }

  async function onReplace(e: Event) {
    e.preventDefault();
    if (!detailId) return;
    formError = null;
    const input = (e.target as HTMLFormElement).elements.namedItem(
      'replace',
    ) as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      formError = 'Choose a replacement .json file.';
      return;
    }
    saving = true;
    try {
      const text = await file.text();
      let json: unknown;
      try {
        json = JSON.parse(text);
      } catch {
        formError = 'File is not valid JSON.';
        return;
      }
      await replaceDeviceProfile(detailId, { json });
      await reload();
      await openDetail(detailId);
      if (input) input.value = '';
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Replace failed';
    } finally {
      saving = false;
    }
  }

  async function onDelete(p: DeviceProfileSummary) {
    formError = null;
    const inUse = (p.skuCount ?? 0) > 0;
    const skus = (p.skuCodes ?? []).join(', ') || `${p.skuCount ?? 0} SKU(s)`;
    const ok = confirm(
      inUse
        ? `Delete profile ${p.meterModel} (id ${p.profileId})?\n\nUsed by: ${skus}\n\nBlocked while linked to any SKU — unlink in SKU Manager first.`
        : `Delete profile ${p.meterModel} (id ${p.profileId})?`,
    );
    if (!ok) return;
    saving = true;
    try {
      await deleteDeviceProfile(p.id);
      if (detailId === p.id) {
        detailId = null;
        detailJson = '';
      }
      await reload();
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Delete failed';
    } finally {
      saving = false;
    }
  }

  function chipLabel(p: DeviceProfileSummary): string {
    return `${p.meterModel} · pid ${p.profileId} v${p.profileVersion}`;
  }
</script>

<div class="page">
  <a class="back" href="#/settings">← Settings</a>
  <h2>Profiles</h2>
  <p class="sub">
    Device profile JSON library. Upload and replace files here. Assign profiles to SKUs in
    <a href="#/settings/skus">SKU Manager</a>.
  </p>

  {#if !isStaff}
    <Card title="Staff only">
      <p class="muted">Only admin/staff can manage profiles.</p>
    </Card>
  {:else}
    <Card title="Upload profile" subtitle="Requires profile_id, profile_version, profile_schema_version, meter_model">
      <form class="form" onsubmit={(e) => void onUpload(e)}>
        <label class="field">
          <span class="label">JSON file</span>
          <input class="input" type="file" name="file" accept=".json,application/json" disabled={saving} />
        </label>
        {#if formError}
          <p class="err" role="alert">{formError}</p>
        {/if}
        <div class="actions">
          <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Upload / upsert'}</Button>
        </div>
      </form>
    </Card>

    <Card title="Library" subtitle={loading ? 'Loading…' : `${profiles.length} profile(s)`}>
      {#if error}
        <p class="err" role="alert">{error}</p>
      {:else if loading}
        <p class="muted">Loading…</p>
      {:else if profiles.length === 0}
        <p class="muted">No profiles yet. Upload a JSON file to get started.</p>
      {:else}
        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>Identity</th>
                <th>Used by SKUs</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#each profiles as p (p.id)}
                <tr class:active-row={detailId === p.id}>
                  <td>
                    <div class="id-main">{chipLabel(p)}</div>
                    <div class="id-sub">schema v{p.profileSchemaVersion}</div>
                  </td>
                  <td class="sku-cell">
                    {#if (p.skuCodes?.length ?? 0) > 0}
                      <span class="chips">
                        {#each (p.skuCodes ?? []).slice(0, 4) as code}
                          <span class="chip">{code}</span>
                        {/each}
                        {#if (p.skuCodes?.length ?? 0) > 4}
                          <span class="chip muted-chip">+{(p.skuCodes?.length ?? 0) - 4}</span>
                        {/if}
                      </span>
                    {:else}
                      <span class="muted">—</span>
                    {/if}
                  </td>
                  <td class="row-actions">
                    <button type="button" class="linkish" disabled={saving} onclick={() => void openDetail(p.id)}
                      >View</button
                    >
                    <button
                      type="button"
                      class="linkish danger"
                      disabled={saving}
                      onclick={() => void onDelete(p)}>Delete</button
                    >
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </Card>

    {#if detailId}
      <Card title="Profile detail" subtitle="Read-only JSON · replace file only">
        {#if detailLoading}
          <p class="muted">Loading…</p>
        {:else}
          <pre class="json">{detailJson}</pre>
          <form class="form replace" onsubmit={(e) => void onReplace(e)}>
            <label class="field">
              <span class="label">Replace file (profile_id must match)</span>
              <input
                class="input"
                type="file"
                name="replace"
                accept=".json,application/json"
                disabled={saving}
              />
            </label>
            <div class="actions">
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Replace file'}</Button>
              <button
                type="button"
                class="linkish muted"
                disabled={saving}
                onclick={() => {
                  detailId = null;
                  detailJson = '';
                }}>Close</button
              >
            </div>
          </form>
        {/if}
      </Card>
    {/if}
  {/if}
</div>

<style>
  .page {
    max-width: 56rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }
  .sub {
    margin: 0.35rem 0 0.5rem;
    color: var(--text-muted);
    font-size: 0.95rem;
    line-height: 1.45;
  }
  .sub a {
    color: var(--accent, #3b82f6);
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .label {
    font-size: 0.8rem;
    color: var(--text-muted);
  }
  .input {
    font: inherit;
    padding: 0.5rem 0.65rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border, #2a2a32);
    background: var(--surface-2, #121218);
    color: var(--text);
  }
  .actions {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }
  .err {
    color: #f87171;
    margin: 0;
    font-size: 0.9rem;
  }
  .muted {
    color: var(--text-muted);
  }
  .table-wrap {
    overflow-x: auto;
  }
  .table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  th,
  td {
    text-align: left;
    padding: 0.55rem 0.4rem;
    border-bottom: 1px solid var(--border, #2a2a32);
    vertical-align: top;
  }
  th {
    color: var(--text-muted);
    font-weight: 500;
    font-size: 0.8rem;
  }
  .id-main {
    font-weight: 600;
  }
  .id-sub {
    font-size: 0.8rem;
    color: var(--text-muted);
    margin-top: 0.15rem;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }
  .chip {
    display: inline-block;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    background: var(--surface-2, #1a1a22);
    border: 1px solid var(--border, #2a2a32);
    font-size: 0.75rem;
  }
  .muted-chip {
    color: var(--text-muted);
  }
  .row-actions {
    white-space: nowrap;
    text-align: right;
  }
  .linkish {
    background: none;
    border: none;
    color: var(--accent, #3b82f6);
    cursor: pointer;
    font: inherit;
    padding: 0.15rem 0.35rem;
  }
  .linkish:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .linkish.danger {
    color: #f87171;
  }
  .linkish.muted {
    color: var(--text-muted);
  }
  .active-row {
    background: color-mix(in srgb, var(--accent, #3b82f6) 8%, transparent);
  }
  .json {
    margin: 0 0 1rem;
    padding: 0.75rem;
    max-height: 22rem;
    overflow: auto;
    border-radius: 0.5rem;
    background: var(--surface-2, #0e0e14);
    border: 1px solid var(--border, #2a2a32);
    font-size: 0.75rem;
    line-height: 1.4;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .replace {
    margin-top: 0.5rem;
  }
</style>
